import { injectAesthetic, scrollbarStyles } from './shared-styles.ts';

export class AppModal extends HTMLElement {
    static get observedAttributes() {
        return ['open', 'size', 'layout'];
    }

    private seed: number;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.seed = Math.floor(Math.random() * 10000);
    }

    connectedCallback() {
        this.render();
        this.setupEventListeners();
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue !== newValue) {
            this.render();
        }
    }

    private setupEventListeners() {
        if (!this.shadowRoot) return;

        // Use event delegation for close button
        this.shadowRoot.addEventListener('click', (e: Event) => {
            const target = e.target as HTMLElement;
            if (target.classList.contains('modal-overlay') || target.closest('.close-btn')) {
                this.close();
            }
        });
    }

    close() {
        this.removeAttribute('open');
        this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
    }

    private _renderTimeout: any = null;

    render() {
        if (this._renderTimeout) return;
        this._renderTimeout = setTimeout(() => {
            this._renderInternal();
            this._renderTimeout = null;
        }, 0);
    }

    private _renderInternal() {
        if (!this.shadowRoot) return;

        const open = this.hasAttribute('open');

        this.shadowRoot.innerHTML = `
            ${injectAesthetic(this.seed)}
            <style>
                :host {
                    display: contents;
                }

                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.02);
                    backdrop-filter: blur(2px);
                    display: ${open ? 'flex' : 'none'};
                    align-items: center;
                    justify-content: center;
                    overflow-y: auto;
                    z-index: 10000;
                    animation: fadeIn 0.2s ease-out;
                }

                .modal {
                    background: #111;
                    display: flex;
                    flex-direction: column;
                    width: 95%;
                    max-width: 500px;
                    position: relative;
                    margin: auto;
                    animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                    max-height: calc(100vh - 4rem);
                    color: white;
                    overflow: visible;
                    padding: 0;
                    --border-sketch: rgba(255, 255, 255, 0.4);
                }

                .modal.sketch-base::before { opacity: 0.9 !important; }
                .modal.sketch-base::after { opacity: 0.7 !important; }

                :host([size="lg"]) .modal { max-width: 800px; }
                :host([size="xl"]) .modal { max-width: 1200px; }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 2rem 2rem 1rem 2rem;
                }

                .modal-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: white;
                    margin: 0;
                    flex: 1;
                    display: flex;
                    align-items: center;
                }

                .modal-body {
                    padding: 0 2rem;
                    overflow-y: auto;
                    flex: 1;
                    /* Fix for potential horizontal scroll from children */
                    overflow-x: hidden; 
                }


                .modal-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 1rem;
                    padding: 1.5rem 2rem 2rem 2rem;
                }

                :host([layout="full"]) .modal-actions {
                    justify-content: stretch;
                }

                :host([layout="full"]) .modal-actions ::slotted(*) {
                    flex: 1;
                    width: 100%;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from { transform: translateY(20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }

                @media (max-width: 640px) {
                    .modal {
                        padding: 1.5rem;
                        width: calc(100% - 2rem);
                        margin: 1rem;
                        max-height: 85vh;
                    }
                    .modal-header {
                        margin-bottom: 1rem;
                    }
                    .modal-actions {
                        margin-top: 1.5rem;
                        flex-direction: column;
                    }
                    .modal-actions ::slotted(app-button) {
                        width: 100%;
                    }
                }
                ${scrollbarStyles}
            </style>

            <div class="modal-overlay">
                <div class="modal sketch-base shadow-industrial" role="dialog" aria-modal="true">
                    <div class="modal-header">
                        <div class="modal-title">
                            <slot name="header">Modal Title</slot>
                        </div>
                    </div>

                    <div class="modal-body">
                        <slot></slot>
                    </div>

                    <div class="modal-actions">
                        <slot name="actions"></slot>
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('app-modal', AppModal);
